import { useEffect, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';
import { Box, Container, Grid } from '@mui/material';

import AuthFooter from 'components/AuthFooter';
import { Logo } from 'components/Logo';
import TwoFA from 'components/svg/TwoFA/TwoFA';
import { BrandCodes } from 'components/Theme/types';
import { useConfiguration } from 'contexts/country-config';
import { AppRoute } from 'share/types/routes';
import { useAuth } from 'src/contexts/authentication';

import { ACTION_TYPES, initialState, reducer } from './reducer';
import TwoFactorInput from './TwoFactorInput';

const PAGE_TITLE = 'Verify your email';
const PAGE_SUBTITLE = 'Enter the 4-digit code sent to your email';

interface ContainerProps {
  resendInterval?: number;
  expiryInterval?: number;
}

export function TwoFactorAuthenticationContainer({
  resendInterval = 30,
  expiryInterval = 300
}: ContainerProps) {
  const navigate = useNavigate();
  const auth = useAuth();
  const countryConfig = useConfiguration();
  const brandCode = countryConfig.entity.code.toLowerCase() as BrandCodes;
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sendTwoFactorAuthRequest = async (otpCode = '') => {
    if (Object.keys(auth.twoFactorAuthData).length) {
      dispatch({
        type: ACTION_TYPES.REQUESTING_OTP
      });

      const { username = '', password = '' } = auth.twoFactorAuthData;

      const response = await auth.signIn(username, password, otpCode);
      if (!response) {
        dispatch({ type: ACTION_TYPES.REQUESTED_OTP });
      } else if (response.token) {
        navigate(AppRoute.Index, { replace: true });
      } else if (response.two_factor_auth && otpCode != '') {
        dispatch({ type: ACTION_TYPES.FAILED_OTP });
      } else {
        dispatch({ type: ACTION_TYPES.REQUESTED_OTP });
      }
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!Object.keys(auth.twoFactorAuthData).length) {
      navigate(AppRoute.Login);
    }
  }, [auth.twoFactorAuthData]);

  return (
    <Container disableGutters maxWidth={false}>
      <div
        style={{
          position: 'absolute',
          bottom: '-1%'
        }}
      >
        <img
          src={`https://images.deliveryhero.io/image/fd-sg/corporate/company-admin/Rectangle%20Diagonal.png`}
          style={{
            width: '38%'
          }}
        />
      </div>
      <div
        style={{
          position: 'absolute',
          top: '15%',
          left: '8%',
          pointerEvents: 'none'
        }}
      >
        <TwoFA />
      </div>

      <Grid
        item
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mt: '100px',
          pl: '390px'
        }}
      >
        <Box
          component="div"
          display="flex"
          flexDirection="column"
          alignItems="center"
          width="400px"
        >
          <Logo brandCode={brandCode} style={{ paddingBottom: '24px' }} />
          <Typography type="title-xl" marginBottom="sm">
            {PAGE_TITLE}
          </Typography>
          <Typography type="paragraph-sm" marginBottom="sm">
            {PAGE_SUBTITLE}
          </Typography>
          <TwoFactorInput
            state={state}
            onSubmit={sendTwoFactorAuthRequest}
            resendInterval={resendInterval}
            expiryInterval={expiryInterval}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
          />
        </Box>
        <AuthFooter
          contactEmail={countryConfig.contact_us}
          indexUrl={countryConfig.index}
          brand={countryConfig.entity.brand.name}
        />
      </Grid>
    </Container>
  );
}
